/* eslint-disable react-refresh/only-export-components */
import { moneytransferApi, shoppingApi, TransferStatusValueEnum } from 'app/src/services/welfareportal'
import {
  CheckCatalogButton,
  ErrorCancelPage,
  ErrorMessage,
  Image,
  PageWidget,
  PortalConfirmDialog,
} from 'app/src/shared/components'
import { CancelSuccess } from 'app/src/shared/components/CancelSuccess'
import { euroFormatterIt, formatDateDDMMYYYY } from 'app/src/shared/helpers'
import { useWebAppContext, useWelfarePortalNavigate } from 'app/src/shared/hooks'
import errorBlock from 'app/static/images/error_block.png'
import successImage from 'app/static/images/success_img.png'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { ProgressBar } from 'primereact/progressbar'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { number, object, ref, string } from 'yup'

export enum TransferFondoPensioneWizardStepEnum {
  REQUEST_FORBIDDEN = -1,
  FIRST_STEP = 0,
  SECOND_STEP = 1,
  FINAL_STEP_SUBMITTED = 2,
  FINAL_STEP_CONFIRMED = 3,
  ERROR_CONFIRM_TRANSFER_REJECTED = 4,
  ERROR_GENERIC = 5,
  DETAIL_STEP = 6,
}

export type FormDataType = {
  welfareCreditNumber?: number
  importToTransferNumber?: number
  importToTransfer?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldValue?: any
}

export function TransferFondoPensioneWizard(props: {
  transferDetails?: moneytransferApi.TransferResponseWithUserInfo
  creditDetails?: shoppingApi.CreditGetResult
  defaultValueImportInserito?: number
  initialStep: TransferFondoPensioneWizardStepEnum
}) {
  const context = useWebAppContext()
  const navigate = useWelfarePortalNavigate()
  const { t } = useTranslation('shopping', { keyPrefix: 'Transfer.FondoPensione' })
  const { t: tr } = useTranslation('shopping', { keyPrefix: 'Transfer' })
  const [wizardStep, setWizardStep] = useState<TransferFondoPensioneWizardStepEnum>(props.initialStep)
  const inputTextStyle = 'col-span-1 min-w-[250px] rounded-none  border-[1px] border-solid border-[#605e5c]'
  const inputTextStyleInvalid = 'col-span-1 min-w-[250px] rounded-none  border-[1px] border-solid border-[#ef4444]'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formErrors, setFormErrors] = useState<any>()
  const [formData, setFormData] = useState<FormDataType>({})
  const defaultValueImportToTransfer = useRef<string>(initDefaultValueImportToTransfer())
  const [showModalConfirmSecondStep, setShowModalConfirmSecondStep] = useState<boolean>(false)
  const [showModalCancel, setShowModalCancel] = useState<boolean>(false)
  const [transferConfirmResponse, setTransferConfirmResponse] = useState<moneytransferApi.TransferResponse>()

  const [showModal, setShowModal] = useState(false)
  const deleteOrder = shoppingApi.useOrderCancel()
  const [errorComponentVisible, setErrorComponentVisible] = useState(false)
  const [successComponentVisible, setSuccessComponentVisible] = useState(false)
  const handleOrderCancellation = async () => {
    try {
      await deleteOrder.mutateAsync({
        pathParams: { orderId: props.transferDetails?.orderId ?? '' },
      })
      setShowModal(false)
      setSuccessComponentVisible(true)
    } catch (error) {
      setErrorComponentVisible(true)
      console.error('Failed to cancel order:', error)
    }
  }
  const REGEX_Importo = /^-?(?!0\d)\d{1,10}(?:\.\d{3})*,\d{2}$/g
  const ERR_Required = t('Errors.ERR_Required')
  const ERR_SaldoRequired = t('Errors.ERR_SaldoRequired')
  const ERR_CreditoInsufficiente = t('Errors.ERR_CreditoInsufficiente')
  const ERR_Importo = t('Errors.ERR_Importo')
  const ERR_Positive = t('Errors.ERR_Positive')

  const yupSchema = object().shape({
    importToTransferNumber: number().required(ERR_Required).positive(ERR_Positive),
    importToTransfer: string().required(ERR_Required).matches(REGEX_Importo, ERR_Importo),
    welfareCreditNumber: number().required(ERR_SaldoRequired).min(ref('importToTransferNumber'), ERR_CreditoInsufficiente),
  })

  const createFamilyMemberMutation = moneytransferApi.useTransferConfirm({
    onError: (err) => {
      context.logger?.error('Error Creating Family Member', { err })
      //if (err.status === 400) setGeneralError('BAD REQUEST')
      //else setGeneralError('GENERIC ERROR')
    },
  })

  const confirmTransfer = useCallback(
    async (requestBody: moneytransferApi.TransferConfirm) => {
      const mutateResp = await createFamilyMemberMutation.mutateAsync({
        pathParams: {
          transferId: props.transferDetails?.id ?? '',
        },
        body: {
          amount: requestBody.amount,
          familyMembers: requestBody.familyMembers,
        },
      })

      return mutateResp
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createFamilyMemberMutation],
  )

  const modalCancel = useMemo(() => {
    return (
      <div className='col-span-12'>
        <PortalConfirmDialog
          accept={async () => navigate.goToCatalog()}
          reject={() => setShowModalCancel(false)}
          yesText={t('WizardSecondStep.modalCancelBtnConfirmLabel')}
          cancelText={t('WizardSecondStep.modalCancelBtnCancelLabel')}
          title={t('WizardSecondStep.modalCancelTitle')}
          body={null}
        />
      </div>
    )
  }, [navigate, t])

  function initDefaultValueImportToTransfer(): string {
    if (props.defaultValueImportInserito)
      return props.defaultValueImportInserito?.toFixed(2)?.replace('.', ',') //in caso di Indietro dalla schermata successiva
    else return '0,00'
  }

  useEffect(() => {
    setFormData({
      welfareCreditNumber: props.creditDetails?.creditByServiceCategory['welfareArt100'].available,
      //campo di tipo number che viene passato al backend (con parte decimale separata da ".")
      importToTransferNumber: props.defaultValueImportInserito
        ? props.defaultValueImportInserito
        : parseFloat(defaultValueImportToTransfer.current?.replace(',', '.')),
      //campo di tipo string che viene usato dallo schema validation (con parte decimale separata da ",") per validare l'input inserito dall'utente
      importToTransfer: props.defaultValueImportInserito
        ? props.defaultValueImportInserito?.toFixed(2)?.replace('.', ',')
        : defaultValueImportToTransfer.current,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onClickContinueFirstStep = () => {
    try {
      yupSchema.validateSync(formData, { abortEarly: false })
      setFormErrors(null)
      setWizardStep(TransferFondoPensioneWizardStepEnum.SECOND_STEP)
      //props.goToSecondStep(formData['importToTransferNumber'])

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const { inner } = err
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const _formErrors: any = {}

      if (inner && inner[0]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        inner.forEach((error: any) => {
          const { path, message } = error

          if (!_formErrors[path]) {
            _formErrors[path] = message
          }
        })
      }
      setFormErrors(_formErrors)
      return
    }
  }

  function renderFirstStep() {
    return (
      <PageWidget>
        <div className='grid columns-12'>
          <Button
            link
            onClick={() => setShowModalCancel(true)}
            icon='pi pi-arrow-left'
            pt={{ root: { className: 'mb-2 pl-0 min-w-[200px]' } }}
          >
            <span className='ml-2 font-bold'>{tr('abortRequestBtnLabel')}</span>
          </Button>
          <div className='col-span-12 text-[18px] font-semibold leading-5'>{t('WizardFristStep.labelStep')}</div>
          <div className='col-span-12 mt-2'>
            <ProgressBar className='h-[10px] rounded-none' showValue={false} value={50}></ProgressBar>
          </div>
          <div className='col-span-12 mt-11 text-left text-2xl font-semibold leading-9'>
            <p>{t('WizardFristStep.title')}</p>
          </div>
          <div className='col-span-12 mt-10 text-left text-base font-normal leading-9'>
            <p>{t('WizardFristStep.subtitle')}</p>
          </div>
        </div>
        <div className='mt-6 grid columns-12'>
          <div className='col-span-12 mt-5 text-left text-sm font-normal leading-4'>{t('WizardFristStep.labelProduct')}</div>
          <div className='col-span-12 mt-5 text-left text-base font-normal leading-5'>{props.transferDetails?.productName}</div>
        </div>
        <div className='mt-6 grid columns-12'>
          <label
            htmlFor='importToTransfer'
            className='col-span-12 mb-[10px] mt-5 py-[5px] text-left text-sm font-normal leading-4'
          >
            {t('WizardFristStep.labelImport')}
          </label>
          <InputText
            className={
              formErrors?.importToTransfer || formErrors?.importToTransferNumber || formErrors?.welfareCreditNumber
                ? inputTextStyleInvalid
                : inputTextStyle
            }
            id='importToTransfer'
            value={formData?.importToTransfer} //{formData?.fieldValue}
            aria-describedby='importToTransfer'
            defaultValue={defaultValueImportToTransfer.current}
            invalid={formErrors && formErrors.length > 0}
            onChange={(evt) =>
              setFormData({
                ...formData,
                importToTransferNumber: parseFloat(evt?.currentTarget?.value?.replace(',', '.')),
                importToTransfer: evt?.currentTarget?.value,
              })
            }
            // onFocus={() => formik.setFieldTouched('importToTransfer')}
            // onBlur={formik.handleBlur}
          />
          <div className='col-span-12 mt-4 text-sm font-normal'>
            {t('WizardFristStep.creditAvailableLabel')}
            {props.creditDetails?.creditByServiceCategory['welfareArt100']?.available && (
              <b>{euroFormatterIt.format(props.creditDetails?.creditByServiceCategory['welfareArt100']?.available)}</b>
            )}
          </div>
          <div className='col-span-12'>
            <ErrorMessage
              errorMessage={formErrors?.importToTransfer ?? formErrors?.importToTransferNumber ?? formErrors?.welfareCreditNumber}
            />
          </div>
        </div>
        <div className='mt-10 flex flex-col items-center'>
          <Button
            rounded
            className='h-[38px] min-w-[150px] text-[14px] font-normal'
            label={t('WizardFristStep.btnLabel')}
            onClick={onClickContinueFirstStep}
          />
        </div>
        {showModalCancel && modalCancel}
      </PageWidget>
    )
  }

  const onClickConfirmSecondStep = async () => {
    try {
      const body: moneytransferApi.TransferConfirm = {
        amount: formData['importToTransferNumber'],
        familyMembers: [],
      }
      const transferConfirmResp: moneytransferApi.TransferResponse = await confirmTransfer(body)
      setTransferConfirmResponse(transferConfirmResp)
      if (transferConfirmResp.status.value == TransferStatusValueEnum.SUBMITTED.toString())
        setWizardStep(TransferFondoPensioneWizardStepEnum.FINAL_STEP_SUBMITTED)
      else if (transferConfirmResp.status.value == TransferStatusValueEnum.REJECTED.toString())
        setWizardStep(TransferFondoPensioneWizardStepEnum.ERROR_CONFIRM_TRANSFER_REJECTED)
    } catch (err) {
      context.logger?.error('Error Confirm Transfer', { err })
      setWizardStep(TransferFondoPensioneWizardStepEnum.ERROR_GENERIC)
    }
  }

  function renderSecondStep() {
    return (
      <PageWidget>
        <div className='grid columns-12'>
          <Button
            link
            onClick={() => setWizardStep(TransferFondoPensioneWizardStepEnum.FIRST_STEP)}
            icon='pi pi-arrow-left'
            pt={{ root: { className: 'mb-2 pl-0 min-w-[150px]' } }}
          >
            <span className='ml-2 font-bold'>{tr('goBackBtnLabel')}</span>
          </Button>
          <div className='col-span-12 text-[18px] font-semibold leading-5'> {t('WizardSecondStep.labelStep')}</div>
          <div className='col-span-12 mt-2'>
            <ProgressBar className='h-[10px] rounded-none' showValue={false} value={100}></ProgressBar>
          </div>
          <div className='col-span-12 mt-5 text-left text-2xl font-semibold leading-9'>
            <p>{t('WizardSecondStep.title')}</p>
          </div>
          <div className='col-span-12 md:col-span-4 md:col-start-1'>
            <div className='mt-6 text-left text-lg font-normal leading-7'>{t('WizardSecondStep.labelProduct')}</div>
            <div className='mt-5 text-left text-2xl font-semibold leading-7'>{props.transferDetails?.productName}</div>
          </div>
          <div className='col-span-12 md:col-span-7 md:col-start-5'>
            <div className='mt-6 text-left text-lg font-normal leading-7'>{t('WizardSecondStep.labelImport')}</div>
            <div className='mt-5 text-left text-[26px] font-bold leading-9'>
              <b>{euroFormatterIt.format(formData['importToTransferNumber']!)}</b>
            </div>
            <div className='mt-4 text-sm font-normal'>
              {t('WizardSecondStep.creditAvailableLabel')}
              {props.creditDetails?.creditByServiceCategory['welfareArt100']?.available && (
                <b>{euroFormatterIt.format(props.creditDetails?.creditByServiceCategory['welfareArt100']?.available)}</b>
              )}
            </div>
          </div>
          <div className='col-span-12 mt-9'>
            <hr className='border-t-solid border-t-[1px] border-[#0047BB]'></hr>
          </div>
          <div className='col-span-12 mt-9'>
            <div className='text-lg font-bold leading-7'>{t('WizardSecondStep.detailTitle')}</div>
            <div className='mt-6 text-sm font-normal leading-4'>{t('WizardSecondStep.detailBenecifiaryLabel')}</div>
            <div className='mt-4 text-[16px] font-normal leading-5'>
              {props.transferDetails?.userInfo.firstName} {props.transferDetails?.userInfo.lastName}
            </div>
          </div>
          <div className='col-span-12 mt-12 flex flex-col items-center'>
            <Button
              rounded
              className='h-[38px] min-w-[150px] text-[14px] font-normal'
              label={t('WizardSecondStep.btnConfirmLabel')}
              onClick={() => setShowModalConfirmSecondStep(true)}
            />
            <Button
              label={t('WizardSecondStep.btnCancelLabel')}
              text
              className='mt-[32px] p-0 text-left'
              pt={{
                label: { className: 'text-[18px] text-[#323130] underline font-normal leading-[22px]' },
              }}
              onClick={() => setShowModalCancel(true)}
            />
          </div>
          {showModalConfirmSecondStep && (
            <div className='col-span-12'>
              <PortalConfirmDialog
                accept={async () => onClickConfirmSecondStep()}
                reject={() => setShowModalConfirmSecondStep(false)}
                yesText={t('WizardSecondStep.modalConfirmBtnConfirmLabel')}
                cancelText={t('WizardSecondStep.modalConfirmBtnCancelLabel')}
                title={t('WizardSecondStep.modalConfirmTitle')}
                body={null}
              />
            </div>
          )}
          {showModalCancel && modalCancel}
        </div>
      </PageWidget>
    )
  }

  function renderFinalStepSubmitted() {
    return (
      <PageWidget>
        <div>
          <div>
            <Image
              pt={{ image: { className: 'object-fit mx-auto h-[150px] lg:h-[250px]' } }}
              src={successImage}
              alt='Immagine di successo'
            />
          </div>
          <div className='text-center text-[28px] font-bold leading-10'>
            <p>{t('WizardFinalStepSubmitted.title')}</p>
            <p>{t('WizardFinalStepSubmitted.subtitle')}</p>
          </div>
          <div className='mt-5 text-center text-[18px] font-normal leading-6'>
            <p>{t('WizardFinalStepSubmitted.description')}</p>
          </div>
          <div className='mx-auto mt-8 flex justify-center'>
            <CheckCatalogButton />
          </div>
        </div>
      </PageWidget>
    )
  }

  function renderErrorGeneric() {
    return (
      <PageWidget>
        <div className='mb-2 flex flex-col'>
          <div className='mx-auto'>
            <div>
              <Image
                pt={{ image: { className: 'max-h-[257px]' } }}
                className='object-fit aspect-[3/2] max-h-[257px]'
                src={errorBlock}
                alt='Immagine di errore'
              />
            </div>
          </div>
          <div className='bold m-auto mx-auto mt-[24px] max-w-xl text-center text-[22px] font-bold leading-[40px]'>
            {t('WizardErrorGeneric.title')}
          </div>
          <div className='mx-auto flex flex-col items-center justify-center'>
            {/* <Button
              rounded
              className='mt-10 h-[38px] min-w-[150px] text-[14px] font-normal'
              label={t('WizardErrorGeneric.btnRetryLabel')}
              //onClick={}
            /> */}
            <Button
              rounded
              label={t('WizardErrorGeneric.btnGoToCatalogLabel')}
              className='my-5 h-[38px] min-w-[150px] text-[14px] font-normal'
              onClick={navigate.goToCatalog}
            />
          </div>
        </div>
      </PageWidget>
    )
  }

  function renderErrorConfirmTransferRejected() {
    return (
      <PageWidget>
        <div className='mb-2 flex flex-col'>
          <div className='mx-auto'>
            <div>
              <Image
                pt={{ image: { className: 'max-h-[257px]' } }}
                className='object-fit aspect-[3/2] max-h-[257px]'
                src={errorBlock}
                alt='Immagine di errore'
              />
            </div>
          </div>
          <div className='bold m-auto mx-auto mt-[24px] max-w-xl text-center text-[22px] font-bold leading-[40px]'>
            <p>{tr('errorConfirmTitle')}</p>
            <p>{transferConfirmResponse?.status?.message}</p>
          </div>
          <div className='mx-auto flex flex-col items-center justify-center'>
            {/* <Button
              rounded
              className='mt-10 h-[38px] min-w-[150px] text-[14px] font-normal'
              label={t('WizardErrorGeneric.btnRetryLabel')}
              //onClick={}
            /> */}
            <Button
              rounded
              label={t('WizardErrorGeneric.btnGoToCatalogLabel')}
              className='my-5 h-[38px] min-w-[150px] text-[14px] font-normal'
              onClick={navigate.goToCatalog}
            />
          </div>
        </div>
      </PageWidget>
    )
  }

  function renderStatus() {
    switch (props.transferDetails?.status.value) {
      case TransferStatusValueEnum.SUBMITTED.toString():
        return (
          <div className='mt-[25px] w-fit bg-[#EEDC00] p-2 text-sm font-bold leading-3 text-[#0047BB]'>
            {tr('StateMapping.SUBMITTED.label')}
          </div>
        )
        break
      case TransferStatusValueEnum.CONFIRMED.toString():
        return (
          <div className='mt-[25px] w-fit bg-[#EEDC00] p-2 text-sm font-bold leading-3 text-[#0047BB]'>
            {tr('StateMapping.CONFIRMED.label')}
          </div>
        )
        break
      case TransferStatusValueEnum.CANCELED.toString():
        return (
          <div className='mt-[25px] w-fit bg-[#fae8eb] p-2 text-sm font-bold leading-3 text-[#BF0B20]'>
            {tr('StateMapping.CANCELED.label')}
          </div>
        )
        break
      case TransferStatusValueEnum.REJECTED.toString():
        return (
          <div className='mt-[25px] w-fit bg-[#fae8eb] p-2 text-sm font-bold leading-3 text-[#BF0B20]'>
            {tr('StateMapping.REJECTED.label')}
          </div>
        )
        break
      default:
        return <div className='mt-[25px] w-fit bg-[#EEDC00] p-2 text-sm font-bold leading-3 text-[#0047BB]'>Non valido</div>
        break
    }
  }

  function renderErrorBox() {
    if (props.transferDetails?.status.value === TransferStatusValueEnum.REJECTED.toString()) {
      return (
        <div className='mt-8 grid grid-cols-12 bg-[#EAEAEA] p-5'>
          <div className='col-span-12 mt-2 text-sm font-bold leading-4'>{tr('Detail.errorTitle')}</div>
          <div className='col-span-12 mt-2 text-sm font-normal leading-4'>{tr('Detail.errorDescription')}</div>
          <div className='col-span-12 mt-2 text-sm font-normal leading-4'>{props.transferDetails?.status?.message}</div>
        </div>
      )
    } else return
  }

  function renderDetail() {
    return (
      <PageWidget>
        <div>
          <div className='text-center'>
            <p className='text-2xl font-semibold leading-9'>{tr('Detail.title')}</p>
          </div>
          {props.transferDetails?.isCancellable && props.transferDetails.status.value !== 'CANCELED' && (
            <div className='flex justify-end'>
              <Button link onClick={() => setShowModal(true)} icon='pi pi-trash' label={t('cancelOrder.cancelButtonLabel')} />
            </div>
          )}
          <div className='mt-9 grid grid-cols-12 md:grid-cols-3'>
            <div className='col-span-12 md:col-span-1'>
              {renderStatus()}
              <div className='mt-5 text-2xl font-semibold leading-7'>{props.transferDetails?.productName}</div>
            </div>
            <div className='col-span-12 flex flex-col items-center max-md:hidden md:col-span-1'>
              <hr className='h-[150px] w-[1px] border-[1px] border-solid border-[var(--primary-color)]'></hr>
            </div>
            <div className='col-span-12 flex flex-col items-center md:col-span-1 md:hidden'>
              <hr className='my-10 w-full rotate-0 border-[1px] border-solid border-[var(--primary-color)]'></hr>
            </div>
            <div className='col-span-12 md:col-span-1'>
              <div className='mt-1 text-left text-lg font-normal leading-7 md:mt-6'>{tr('Detail.labelImport')}</div>
              <div className='mt-5 text-left text-[26px] font-bold leading-9'>
                <b>{props.transferDetails?.amount ? euroFormatterIt.format(props.transferDetails.amount!) : ''}</b>
              </div>
            </div>
          </div>
          {renderErrorBox()}
          <div className='mt-9 grid grid-cols-12 bg-[#F2F8FF] p-5'>
            <div className='col-span-12'>
              <div className='w-full break-words text-sm font-normal leading-4'>
                {tr('Detail.orderIdLabel')}{' '}
                <span className='mt-4 text-[16px] font-bold leading-5'>{props.transferDetails?.id ?? '-'}</span>
              </div>
              <div className='my-4 h-px w-full bg-black'></div>
            </div>
            <div className='col-span-12 md:col-span-6'>
              <div className='mt-2 text-sm font-normal leading-4'>{tr('Detail.detailBenecifiaryLabel')}</div>
              <div className='mt-4 text-[16px] font-bold leading-5'>
                {props.transferDetails?.userInfo.firstName} {props.transferDetails?.userInfo.lastName}
              </div>
            </div>
            <div className='col-span-12 md:col-span-6 md:ml-2'>
              <div className='mt-2 text-sm font-normal leading-4'>{tr('Detail.detailDate')}</div>
              <div className='mt-4 text-[16px] font-bold leading-5'>
                {props.transferDetails?.date ? formatDateDDMMYYYY(new Date(props.transferDetails.date!), true) : ''}
              </div>
            </div>
          </div>
        </div>
        <div className='mx-auto flex justify-center'>
          <CheckCatalogButton />
        </div>
        {showModal && (
          <PortalConfirmDialog
            body={t('cancelOrder.body')}
            title={t('cancelOrder.title')}
            reject={() => setShowModal(false)}
            accept={handleOrderCancellation}
          />
        )}
      </PageWidget>
    )
  }
  if (errorComponentVisible) {
    return (
      <ErrorCancelPage
        errorTitle={t('cancelOrder.cancelErrorMessage.title')}
        errorSubtitle={t('cancelOrder.cancelErrorMessage.subtitle')}
      >
        <div className='mx-auto flex justify-center'>
          <CheckCatalogButton />
        </div>
      </ErrorCancelPage>
    )
  }
  if (successComponentVisible) {
    return (
      <CancelSuccess
        successTitle={t('cancelOrder.cancelSuccessMessage.title')}
        successSubTitle={t('cancelOrder.cancelSuccessMessage.subtitle')}
      >
        <div className='mx-auto flex justify-center'>
          <CheckCatalogButton />
        </div>
      </CancelSuccess>
    )
  }
  if (deleteOrder.isPending)
    return (
      <div className='mt-2 flex'>
        <div className='mx-auto'>
          <ProgressSpinner
            data-testid='loading-element'
            pt={{
              root: {
                className: 'w-[50px] h-[50px]',
              },
            }}
          />
        </div>
      </div>
    )

  switch (wizardStep) {
    case TransferFondoPensioneWizardStepEnum.FIRST_STEP:
      return <PageWidget>{renderFirstStep()}</PageWidget>
      break
    case TransferFondoPensioneWizardStepEnum.SECOND_STEP:
      return <PageWidget>{renderSecondStep()}</PageWidget>
      break
    case TransferFondoPensioneWizardStepEnum.FINAL_STEP_SUBMITTED:
      return <PageWidget>{renderFinalStepSubmitted()}</PageWidget>
      break
    case TransferFondoPensioneWizardStepEnum.ERROR_GENERIC:
      return <PageWidget>{renderErrorGeneric()}</PageWidget>
      break
    case TransferFondoPensioneWizardStepEnum.ERROR_CONFIRM_TRANSFER_REJECTED:
      return <PageWidget>{renderErrorConfirmTransferRejected()}</PageWidget>
      break
    case TransferFondoPensioneWizardStepEnum.DETAIL_STEP:
      return <PageWidget>{renderDetail()}</PageWidget>
      break
    default:
      break
  }
}
